import { useAuth } from "@/features/auth/hooks/useAuth";
import { ClipboardDocumentFolder } from "@/features/clipboard/components/ClipboardDocumentFolder";
import { ClipboardFileTable } from "@/features/clipboard/components/ClipboardFileTable";
import { ClipboardUploadTray } from "@/features/clipboard/components/ClipboardUploadTray";
import { useClipboardRoute } from "@/features/clipboard/hooks/useClipboardRoute";
import { useClipboardUploads } from "@/features/clipboard/hooks/useClipboardUploads";
import { useEffect, type MutableRefObject } from "react";

export type ClipboardUploadActions = {
  onUploadFiles: () => void;
  onUploadFolder: () => void;
  onImportFiles: (files: File[]) => void;
};

type ClipboardFilesPaneProps = {
  onError: (message: string) => void;
  uploadActionsRef: MutableRefObject<ClipboardUploadActions | null>;
};

export const ClipboardFilesPane = ({
  onError,
  uploadActionsRef,
}: ClipboardFilesPaneProps) => {
  const { user } = useAuth();
  const { path, routeDocumentId } = useClipboardRoute();
  const {
    uploads,
    dragActive,
    setDragActive,
    onDrop,
    runUploads,
    retryUpload,
    dismissUpload,
    pdfInputRef,
    folderInputRef,
    storageAtLimit,
  } = useClipboardUploads({ path, user, onError });

  useEffect(() => {
    uploadActionsRef.current = {
      onUploadFiles: () => pdfInputRef.current?.click(),
      onUploadFolder: () => folderInputRef.current?.click(),
      onImportFiles: (files) => {
        if (files.length) void runUploads(files);
      },
    };
    return () => {
      uploadActionsRef.current = null;
    };
  }, [uploadActionsRef, pdfInputRef, folderInputRef, runUploads]);

  return (
    <>
      {/* Upload inputs are triggered from the left sidebar's Upload menu
          (see ClipboardActions); kept hidden here with runUploads. */}
      <input
        ref={pdfInputRef}
        type="file"
        multiple
        className="hidden"
        onChange={(e) => {
          if (e.target.files) void runUploads(e.target.files);
          e.target.value = "";
        }}
      />
      <input
        ref={folderInputRef}
        type="file"
        multiple
        className="hidden"
        {...({ webkitdirectory: "", directory: "" } as React.InputHTMLAttributes<HTMLInputElement>)}
        onChange={(e) => {
          if (e.target.files) void runUploads(e.target.files);
          e.target.value = "";
        }}
      />

      <ClipboardUploadTray
        uploads={uploads}
        onRetry={retryUpload}
        onDismiss={dismissUpload}
      />

      {routeDocumentId ? (
        <ClipboardDocumentFolder onError={onError} />
      ) : (
        <ClipboardFileTable
          dragActive={dragActive}
          onDragOver={(e) => {
            if (storageAtLimit) return;
            e.preventDefault();
            setDragActive(true);
          }}
          onDragLeave={() => setDragActive(false)}
          onDrop={(e) => {
            if (storageAtLimit) {
              e.preventDefault();
              return;
            }
            void onDrop(e);
          }}
          onError={onError}
        />
      )}
    </>
  );
};
